import { Icon, cx, useFlashOnChange } from "../../components/ui";
import { NAMA_KOLOM, SKALA, pilihanKomentar, teksAcuan } from "./logika";

// Satu aspek dalam satu bulan: tombol NI–O (sekali ketuk, bukan dropdown), nilai bulan yang
// muncul sendiri, deskripsi nilainya, lalu kalimat komentar yang cocok TEPAT di bawahnya —
// supaya trainer tak bolak-balik antar-halaman seperti di sistem lama (§7.1).
// Ketuk nilai yang sedang aktif = hapus nilai itu.

export default function KartuAspek({
  bank, rubrik, aspek, label, satuNilai, data, acuan, bolehUbah, urutan = 0, onNilai, onKomentar
}) {
  const kilat = useFlashOnChange(data.nilai);
  const labelSkala = Object.fromEntries((bank.skala || []).map((s) => [s.kode, s.label]));
  const deskripsi = data.nilai ? (((bank.rubrik[rubrik] || {}).deskripsi || {})[aspek] || {})[data.nilai] : "";
  const opsi = data.nilai ? pilihanKomentar(bank, rubrik, aspek, data.nilai) : [];
  const perluUlang = data.komentar && data.komentar.pilihan != null && !data.komentar.sah && !!data.nilai;

  return (
    <section
      aria-label={label}
      className={cx(
        "anim-rise rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900",
        urutan < 4 && `anim-d${urutan + 1}`
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-black text-slate-900 dark:text-white">{label}</h3>
        <span
          className={cx(
            "rounded-full px-2.5 py-1 text-xs font-black",
            kilat && "anim-flash",
            data.nilai ? "bg-primary/10 text-primary dark:text-[#3987e5]" : "bg-slate-100 text-slate-400 dark:bg-slate-800"
          )}
        >
          {data.nilai ? `${data.nilai} · ${labelSkala[data.nilai] || ""}` : "Belum ada nilai"}
        </span>
      </div>

      {satuNilai && (
        <p className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 p-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
          <Icon name={aspek === "attendance" ? "event_available" : "schedule"} className="text-base text-primary" />
          <span>
            <b>Acuan:</b> {teksAcuan(aspek, acuan)}
            <span className="block text-[11px] text-slate-400">Dari data absensi kelas ini. Nilainya tetap kamu yang menentukan.</span>
          </span>
        </p>
      )}

      {(satuNilai ? [1] : [1, 2, 3]).map((kolom) => {
        const namaKolom = satuNilai ? "Nilai" : NAMA_KOLOM[kolom - 1];
        const isi = (data.isian || [])[kolom - 1] || "";
        return (
          <div key={kolom} className="mt-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{namaKolom}</p>
            <div className="mt-1 grid grid-cols-5 gap-1.5" role="group" aria-label={`${label} — ${namaKolom}`}>
              {SKALA.map((g) => {
                const aktif = isi === g;
                return (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={aktif}
                    aria-label={`${namaKolom}: ${labelSkala[g] || g}`}
                    title={labelSkala[g]}
                    disabled={!bolehUbah}
                    onClick={() => onNilai(kolom, aktif ? null : g)}
                    className={cx(
                      "lift-press min-h-[2.5rem] rounded-lg border text-sm font-black disabled:cursor-not-allowed disabled:opacity-60",
                      aktif
                        ? "border-primary bg-primary text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:border-primary/60 hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    )}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {deskripsi && <p className="mt-2 text-xs text-slate-500">{deskripsi}</p>}

      <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-800">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Kalimat komentar</p>
        {!data.nilai ? (
          <p className="mt-1 text-xs text-slate-400">Isi nilainya dulu — pilihan kalimat mengikuti nilai bulan ini.</p>
        ) : (
          <>
            {perluUlang && (
              <p className="mt-1 flex items-center gap-1 text-xs font-bold text-amber-700 dark:text-amber-300">
                <Icon name="refresh" className="text-sm" />
                Nilainya berubah — pilih ulang kalimatnya.
              </p>
            )}
            <div className="mt-2 space-y-2" role="group" aria-label={`Kalimat komentar ${label}`}>
              {opsi.map((teks, i) => {
                const dipilih = !!data.komentar && data.komentar.sah && data.komentar.pilihan === i;
                return (
                  <button
                    key={i}
                    type="button"
                    aria-pressed={dipilih}
                    disabled={!bolehUbah}
                    onClick={() => { if (!dipilih) onKomentar(i); }}
                    className={cx(
                      "flex w-full items-start gap-2 rounded-xl border p-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed",
                      dipilih
                        ? "border-primary bg-primary/10 text-slate-900 dark:text-white"
                        : "border-slate-200 text-slate-600 hover:border-primary/50 dark:border-slate-700 dark:text-slate-300"
                    )}
                  >
                    <Icon
                      name={dipilih ? "check_circle" : "radio_button_unchecked"}
                      className={cx("mt-0.5 text-base", dipilih ? "text-primary" : "text-slate-300")}
                    />
                    <span>{teks}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
